/**
 * Regras de tela da pista para os descontos novos do pedido de 19/10 (Fase 2).
 *
 * - Prêmio do sorteio (tipo "sorteio", id "sorteio:<premio_id>"): uso único, NUNCA pré-selecionado,
 *   não cumulativo. Escolhido o prêmio, a troca para Aditivada não vale nesse abastecimento e o
 *   grupo de consumo cumulativo não soma (decisão dos itens 2 e 3).
 * - Desconto progressivo (tipo "progressivo"): X ¢/L a partir de R$ Y. Base do mínimo: modo R$ =
 *   valor digitado; modo litros = litros x preço de cadastro (antes de qualquer desconto). Abaixo do
 *   mínimo o item fica desabilitado; o calcular do backend revalida e avisa.
 *
 * - Rota de Fidelização (Fase 3, item 10): item da etapa (tipo "rota_etapa_N") e prêmio da Rota
 *   (tipo "rota_premio", R$ como o cashback). Os dois são NÃO cumulativos: não somam Aditivada nem
 *   grupo cumulativo. Durante as etapas (cliente em rota, com ou sem desconto hoje) nada soma.
 *   O prêmio da Rota nunca vem pré-selecionado.
 *
 * O backend é a fonte da verdade: estas funções só decidem o que a tela mostra/habilita.
 */

/** Desconto escolhido é o prêmio do sorteio (pelo tipo ou pelo id, como o backend baixa). */
export function ehSorteio(d) {
  if (!d) return false;
  return d.tipo === "sorteio" || String(d.id || "").startsWith("sorteio:");
}

export function ehProgressivo(d) {
  return !!d && (d.tipo === "progressivo" || d.id === "progressivo");
}

/** Item da etapa da Rota de Fidelização ("rota_etapa_1".."rota_etapa_3"). */
export function ehRotaEtapa(d) {
  if (!d) return false;
  return String(d.tipo || "").startsWith("rota_etapa_") || String(d.id || "").startsWith("rota_etapa_");
}

/** Prêmio da Rota (saldo em R$ liberado ao concluir as 3 etapas). */
export function ehRotaPremio(d) {
  return !!d && (d.tipo === "rota_premio" || d.id === "rota_premio");
}

/** Desconto que não soma com nada (Aditivada e grupo cumulativo ficam de fora). */
export function ehNaoCumulativo(d) {
  return ehSorteio(d) || ehRotaEtapa(d) || ehRotaPremio(d);
}

/** Descontos em R$ (abatimento do saldo): não mexem no preço por litro. */
export function ehDescontoEmReais(d) {
  return !!d && (d.tipo === "cashback" || d.tipo === "indicacao" || ehRotaPremio(d));
}

/**
 * Desconto que pode vir pré-selecionado na pista. O backend já não escolhe o prêmio do sorteio nem o
 * progressivo como padrão; a tela confere de novo (defesa: o prêmio não pode ser gasto sem o
 * frentista escolher, e o progressivo depende do valor que ainda não foi digitado).
 */
export function padraoPermitido(d) {
  return d && !ehSorteio(d) && !ehProgressivo(d) && !ehRotaPremio(d) ? d : null;
}

/**
 * Troca para Aditivada que vale de fato: aceita pelo cliente, sem desconto não cumulativo escolhido
 * (prêmio do sorteio, etapa ou prêmio da Rota) e fora das etapas da Rota (semCumulativos).
 */
export function aditivadaEfetiva(cumularAceita, chosenDesc, semCumulativos = false) {
  return !!cumularAceita && !ehNaoCumulativo(chosenDesc) && !semCumulativos;
}

/** Base do mínimo do progressivo (R$). Valor inválido = 0. */
export function baseMinimoProgressivo(modo, valor, precoCadastro) {
  const v = Number(valor);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (modo === "litros") {
    const p = Number(precoCadastro);
    return Number.isFinite(p) && p > 0 ? v * p : 0;
  }
  return v;
}

const centavos = (v) => Math.round((Number(v) || 0) * 100);

/** Mínimo exigido pelo item (0 quando não veio). */
export function minimoProgressivo(d) {
  const m = Number(d?.valor_minimo_reais);
  return Number.isFinite(m) && m > 0 ? m : 0;
}

/** Item progressivo atinge o mínimo com a base informada (comparação em centavos). */
export function progressivoElegivel(d, base) {
  return centavos(base) >= centavos(minimoProgressivo(d));
}

/** Quanto falta (R$) para o progressivo valer; 0 quando já vale. */
export function faltaProgressivo(d, base) {
  const falta = centavos(minimoProgressivo(d)) - centavos(base);
  return falta > 0 ? falta / 100 : 0;
}

/**
 * Prévia do preço efetivo por litro na tela de valor (o backend calcula o valor real).
 * REGRA v2.21: o desconto incide sobre o preço de cadastro. Cashback/indicação/prêmio da Rota são
 * R$ (não mexem no preço/L). Aditivada e grupo cumulativo somam, exceto com desconto não cumulativo
 * (prêmio do sorteio, etapa ou prêmio da Rota) e durante as etapas da Rota (semCumulativos).
 */
export function previewPrecoEfetivo({ preco, chosenDesc, cumularAditivada, grupoCumulativo, bonusAditivadaReais = 0.05, semCumulativos = false }) {
  if (!preco) return null;
  let p = Number(preco.preco_cadastro) || 0;
  const semSoma = ehNaoCumulativo(chosenDesc) || !!semCumulativos;
  if (chosenDesc) {
    if (ehDescontoEmReais(chosenDesc)) {
      // R$ off: a prévia mostra só o preço fixo
    } else if (chosenDesc.desconto_centavos_litro) {
      p = p - (Number(chosenDesc.desconto_centavos_litro) / 100);
    }
  }
  if (cumularAditivada && !semSoma) p -= bonusAditivadaReais;
  // v2.29: grupo de consumo com regra "cumulativo" — soma ao escolhido (menos com desconto não cumulativo).
  if (grupoCumulativo && !semSoma) p -= (Number(grupoCumulativo.desconto_centavos_litro) || 0) / 100;
  return Math.max(0, p);
}

/** Aviso(s) devolvidos pelo calcular, como lista de textos. */
export function avisosDoCalculo(calc) {
  const a = calc?.aviso ?? calc?.avisos;
  if (!a) return [];
  return (Array.isArray(a) ? a : [a]).map((x) => String(x || "").trim()).filter(Boolean);
}

// Confirmar (G6): o prêmio do sorteio deixou de valer entre o calcular e o confirmar (usado em outra
// venda, cancelado pelo Master, vencido). Por padrão (regra 1, aprovada pelo cliente em 24/10/2026)
// o backend finaliza COM o desconto calculado e devolve só o "aviso" da ocorrência: nada muda na tela.
// Na regra anterior (MANTER_DESCONTO_SE_INDISPONIVEL=False no backend) ele finaliza SEM o desconto e
// devolve também "valores_ajustados" {valor_bruto, valor_liquido, desconto_total_reais,
// preco_efetivo, litros}; aí a tela troca os valores do calcular pelos ajustados: o frentista cobra o
// valor real e o comprovante sai com ele (o caixa soma o valor_liquido gravado).
const CAMPOS_AJUSTADOS = [
  ["valor_bruto", "valor_bruto"],
  ["valor_liquido", "valor_liquido"],
  ["desconto_total_reais", "desconto_total_reais"],
  ["preco_efetivo", "preco_efetivo_por_litro"],
  ["preco_efetivo_por_litro", "preco_efetivo_por_litro"],
  ["litros", "litros"],
];

/** Valores ajustados que vieram do confirmar (só os numéricos); null quando não vieram. */
export function valoresAjustados(resposta) {
  const a = resposta?.valores_ajustados;
  if (!a || typeof a !== "object") return null;
  const out = {};
  CAMPOS_AJUSTADOS.forEach(([de, para]) => {
    if (a[de] == null || a[de] === "") return;
    const v = Number(a[de]);
    if (Number.isFinite(v)) out[para] = v;
  });
  return Object.keys(out).length ? out : null;
}

/**
 * calc da tela com os valores do confirmar (preco_efetivo vira preco_efetivo_por_litro). Sem
 * valores ajustados devolve o mesmo calc. A venda ficou sem o desconto do prêmio: o tipo sai.
 */
export function aplicarValoresAjustados(calc, resposta) {
  const ajuste = valoresAjustados(resposta);
  if (!ajuste) return calc;
  return { ...(calc || {}), ...ajuste, tipo_desconto: null, ajustado_no_confirmar: true };
}
